import React, { useCallback, useEffect, useRef, useState } from 'react';
import { PlacementService, PlacementCheckResponse, PlacementGenerateResponse } from '@services';
import { useServiceRef } from 'hooks';
import Cookies from 'js-cookie';
import { TENANT_STORAGE_KEY } from '@constants/tenant';
import { Button } from '@components';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import { Modal } from 'gd-design-library';
import { InitialView } from './components/InitialView/InitialView';
import { PreviewView } from './components/PreviewView/PreviewView';

interface PlacementProps {
  productId: string;
}

export const Placement: React.FC<PlacementProps> = ({ productId }) => {
  const placementServiceRef = useServiceRef(PlacementService);
  const pollingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const [ placementCheck, setPlacementCheck ] = useState<PlacementCheckResponse | null>(null);
  const [ placementResult, setPlacementResult ] = useState<PlacementGenerateResponse | null>(null);
  const [ videoBase64, setVideoBase64 ] = useState<string | null>(null);
  const [ videoError, setVideoError ] = useState<string | null>(null);

  const [ isModalOpen, setIsModalOpen ] = useState(false);
  const [ sceneImage, setSceneImage ] = useState<File | null>(null);
  const [ notes, setNotes ] = useState<string>('');

  const [ isGeneratingImage, setIsGeneratingImage ] = useState(false);
  const [ isEditingImage, setIsEditingImage ] = useState(false);

  const stopPolling = useCallback((): void => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  }, []);

  const resetPlacement = (keepResult = false): void => {
    stopPolling();
    if (!keepResult) {
      setPlacementResult(null);
    }
    setSceneImage(null);
    setNotes('');
    setVideoBase64(null);
    setVideoError(null);
  };

  // Check if the product can be placed
  useEffect(() => {
    (async (): Promise<void> => {
      if (typeof productId !== 'string') return;

      const tenantKey = JSON.parse(Cookies.get(TENANT_STORAGE_KEY) || 'null')?.key;
      const result = await placementServiceRef.current.checkPlacement({ productId, tenantKey });
      setPlacementCheck(result);
    })();
  }, [ productId ]);

  // Poll the video status
  useEffect(() => {
    const operationName = placementResult?.videoOperationName;
    if (!operationName || videoBase64 || videoError) {
      return;
    }

    const pollVideoStatus = async (): Promise<void> => {
      const tenantKey = JSON.parse(Cookies.get(TENANT_STORAGE_KEY) || 'null')?.key;
      const status = await placementServiceRef.current.getVideoStatus({
        operationName,
        tenantKey
      });

      if (status?.videoBase64) {
        setVideoBase64(status.videoBase64);
        stopPolling();
      } else if (status?.errorMessage) {
        setVideoError(status.errorMessage);
        stopPolling();
      }
    };

    pollVideoStatus();
    pollingIntervalRef.current = setInterval(pollVideoStatus, 5000);

    return stopPolling;
  }, [ placementResult?.videoOperationName, videoBase64, videoError, placementServiceRef, stopPolling ]);

  // Stop polling when modal closes
  useEffect(() => {
    if (!isModalOpen) {
      stopPolling();
    }
  }, [ isModalOpen, stopPolling ]);

  const generatePlacement = async (generateVideo: boolean): Promise<void> => {
    if (!sceneImage) return;

    setIsGeneratingImage(true);

    const tenantKey = JSON.parse(Cookies.get(TENANT_STORAGE_KEY) || 'null')?.key;
    const result = await placementServiceRef.current.generatePlacement({
      sceneImage,
      productId,
      notes,
      generateVideo,
      tenantKey
    });

    setIsGeneratingImage(false);

    if (result) {
      setPlacementResult(result);
      resetPlacement(true);
    }
  };

  const editPlacement = async (): Promise<void> => {
    if (!placementResult?.imageId) return;

    setIsEditingImage(true);

    const tenantKey = JSON.parse(Cookies.get(TENANT_STORAGE_KEY) || 'null')?.key;
    const result = await placementServiceRef.current.editPlacement({
      imageId: placementResult?.imageId,
      editingPrompt: notes,
      tenantKey
    });

    setIsEditingImage(false);

    if (result) {
      setPlacementResult(result);
      resetPlacement(true);
    }
  };

  return placementCheck?.canBePlaced && (
    <>
      <Button
        variant='outlined'
        iconStart={ <AddPhotoAlternateIcon /> }
        onClick={ (): void => setIsModalOpen(true) }
      >
        See it in your space
      </Button>
      <Modal
        isOpen={ isModalOpen }
        title='See it in your space'
        onClose={ (): void => {
          setIsModalOpen(false);
          resetPlacement();
          stopPolling();
        } }
      >
        {placementResult ? (
          <PreviewView
            placementResult={ placementResult }
            videoBase64={ videoBase64 }
            videoError={ videoError }
            notes={ notes }
            setNotes={ setNotes }
            editPlacement={ editPlacement }
            isEditingImage={ isEditingImage }
            resetPlacement={ resetPlacement }
          />
        ) : (
          <InitialView
            placementCheck={ placementCheck }
            sceneImage={ sceneImage }
            setSceneImage={ setSceneImage }
            notes={ notes }
            setNotes={ setNotes }
            generatePlacement={ generatePlacement }
            isGeneratingImage={ isGeneratingImage }
          />
        )}
      </Modal>
    </>
  );
};

